import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import {
  LINE_BADGE_ARIA_LABEL,
  LINES_AT_STATION,
  MORE_LINES_COUNT,
} from 'src/app/modules/route-planner/constants/multi-language.constants';
import { Route } from 'src/app/modules/route-planner/interfaces/route-planning/gtfs/route.interface';

@Component({
    selector: 'app-stations-result-list-item-lines',
    templateUrl: './stations-result-list-item-lines.component.html',
    styleUrls: ['./stations-result-list-item-lines.component.scss'],
    standalone: false
})
export class StationsResultListItemLinesComponent implements OnInit, OnDestroy {
  @Input() routes: Route[]

  /**
   * a11y: localized "Lines at this station" used as aria-label on the
   * wrapping <ul>. Without it SR users hear an anonymous list of route
   * badges with no clue what they represent.
   */
  linesAtStationLabel: string = '';

  /**
   * a11y: localized template "Line {0}, {1}" used to build the aria-label
   * for each route badge. SR announces "Line 3, Egged" instead of two
   * adjacent tokens (logo alt + route number) which it would otherwise
   * read with no context.
   */
  lineBadgeLabelTemplate: string = '';

  /**
   * a11y: localized template "{0} more lines" used to build the aria-label
   * for the "+N" overflow badge that appears after the 7th line.
   */
  moreLinesLabelTemplate: string = '';

  private langSub?: Subscription;

  constructor(private readonly languageService: LanguageService) {}

  ngOnInit(): void {
    this.langSub = this.languageService.selectedLanguage$.subscribe((language) => {
      this.linesAtStationLabel = LINES_AT_STATION[language.language];
      this.lineBadgeLabelTemplate = LINE_BADGE_ARIA_LABEL[language.language];
      this.moreLinesLabelTemplate = MORE_LINES_COUNT[language.language];
    });
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  /** Build per-route accessible name: "Line {number}, {agency}". */
  getLineBadgeAriaLabel(route: Route): string {
    const number = route.agencyId === 2 ? route.routeDesc : route.routeName;
    return this.lineBadgeLabelTemplate
      .replace('{0}', String(number ?? ''))
      .replace('{1}', route.agencyName ?? '');
  }

  /** Build "+N more lines" accessible name for the overflow badge. */
  getMoreLinesAriaLabel(count: number): string {
    return this.moreLinesLabelTemplate.replace('{0}', String(count));
  }
}
